import { useEffect, useState } from 'react';

/**
 * Client-side pagination for the Users list.
 *
 * Client-side on purpose: the screen streams the whole `users` collection once
 * and searches, filters, counts chips and sorts it in memory (several of those
 * read Auth metadata and push state that Firestore cannot query). A Firestore
 * cursor would page the RAW collection and break every one of them. At a few
 * hundred docs the snapshot is cheap; revisit when it is thousands.
 */
export const USERS_PAGE_SIZE = 25;

export interface PageSlice<T> {
  items: T[];
  /** 1-based, clamped to [1, pageCount]. */
  page: number;
  pageCount: number;
  /** 1-based index of the first item shown; 0 when empty. */
  from: number;
  to: number;
  total: number;
}

export function paginate<T>(all: readonly T[], page: number, pageSize = USERS_PAGE_SIZE): PageSlice<T> {
  const total = all.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const p = Math.min(Math.max(1, Math.floor(page) || 1), pageCount);
  const start = (p - 1) * pageSize;
  const items = all.slice(start, start + pageSize);
  return { items, page: p, pageCount, from: total === 0 ? 0 : start + 1, to: start + items.length, total };
}

interface UsersPagerProps {
  slice: PageSlice<unknown>;
  onPage: (page: number) => void;
}

export function UsersPager({ slice, onPage }: UsersPagerProps) {
  const { page, pageCount, from, to, total } = slice;
  // A draft, so typing "1" on the way to "12" does not jump to page 1.
  const [draft, setDraft] = useState(String(page));
  useEffect(() => setDraft(String(page)), [page]);

  const commit = () => {
    const n = Number(draft);
    if (Number.isFinite(n)) onPage(Math.min(Math.max(1, Math.floor(n)), pageCount));
    else setDraft(String(page));
  };

  const btn =
    'px-2 py-1 text-sm rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <nav
      className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 text-sm text-gray-600"
      aria-label="Users pagination"
    >
      <span>
        {total === 0 ? 'No users' : `Showing ${from}–${to} of ${total}`}
      </span>
      <div className="flex items-center gap-1">
        <button type="button" className={btn} onClick={() => onPage(1)} disabled={page <= 1} aria-label="First page">
          «
        </button>
        <button type="button" className={btn} onClick={() => onPage(page - 1)} disabled={page <= 1} aria-label="Previous page">
          ‹
        </button>
        <span className="flex items-center gap-1 px-1">
          Page
          <input
            type="number"
            min={1}
            max={pageCount}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
            }}
            className="w-14 rounded border border-gray-300 px-1.5 py-0.5 text-sm text-center"
            aria-label="Page number"
          />
          of {pageCount}
        </span>
        <button
          type="button"
          className={btn}
          onClick={() => onPage(page + 1)}
          disabled={page >= pageCount}
          aria-label="Next page"
        >
          ›
        </button>
        <button
          type="button"
          className={btn}
          onClick={() => onPage(pageCount)}
          disabled={page >= pageCount}
          aria-label="Last page"
        >
          »
        </button>
      </div>
    </nav>
  );
}
